import { Loader2, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { platform } from '../lib/native';
import { trackEvent } from '../lib/analytics';
import { getNativePrices } from '../lib/revenuecat';
import { CREDIT_PACK_SMALL, CREDIT_PACK_LARGE } from '../constants/pricing';
import { LOW_CREDIT_UI_THRESHOLD } from '../utils/creditThresholds';
import { ICON_SIZE } from '../design-system';
import { getTierDisplayName } from '@papper/shared/reedle/tiers';

const SHOW_CREDIT_BREAKDOWN = false;

const UsageBalanceTab = ({
  balanceLoading,
  balanceError,
  balance,
  refreshBalance,
  tierCredits,
  permanentCredits,
  effectiveTier,
  selectedCurrency,
  setSelectedCurrency,
  currencyInfo,
  currencyConfig,
  topUpLoading,
  handleTopUp,
  handleNativeTopUp,
  planTiersPro,
  planTiersMax,
  onSwitchToPlan,
  handleRestorePurchases,
  restoreLoading,
}) => {
  const { t } = useTranslation('modals');
  // On native, use StoreKit-sourced prices for upsell banner.
  const nativePrices = platform.isNative ? getNativePrices() : {};

  return (
    <>
      <div className="usage-balance-section">
        {balanceLoading ? (
          <div className="usage-loading">{t('common.loading', { defaultValue: 'Loading...' })}</div>
        ) : balanceError ? (
          <div className="usage-error">
            <div className="usage-balance-label">{t('usage.errors.balanceFetchFailed', { defaultValue: 'Failed to load balance' })}</div>
            <button className="usage-retry-btn" onClick={refreshBalance}>
              {t('common.retry', { defaultValue: 'Retry' })}
            </button>
          </div>
        ) : (
          <>
            <div className="usage-balance-label">{t('usage.creditsRemaining')}</div>
            <div className="usage-balance-value">{balance?.toLocaleString() ?? '--'}</div>

            {/* Credit Breakdown Bar - controlled by SHOW_CREDIT_BREAKDOWN toggle */}
            {SHOW_CREDIT_BREAKDOWN && tierCredits?.balance > 0 && permanentCredits?.balance > 0 && (() => {
              const total = tierCredits.balance + permanentCredits.balance;
              const tierRatio = tierCredits.balance / total;
              const cappedTierFlex = Math.max(0.3, Math.min(0.7, tierRatio));
              const cappedPermanentFlex = 1 - cappedTierFlex;
              return (
                <div className="usage-credit-bar">
                  <div className="usage-credit-segment tier" style={{ flex: cappedTierFlex }}>
                    <span className="segment-value">{tierCredits.balance.toLocaleString()}</span>
                    <span className="segment-label">{t('usage.monthlyCredits', { tier: getTierDisplayName(tierCredits.tier) })}</span>
                  </div>
                  <div className="usage-credit-segment permanent" style={{ flex: cappedPermanentFlex }}>
                    <span className="segment-value">{permanentCredits.balance.toLocaleString()}</span>
                    <span className="segment-label">{t('usage.permanentCredits')}</span>
                  </div>
                </div>
              );
            })()}

            <div className="usage-balance-hint">
              {t('usage.creditsHint')}
            </div>
          </>
        )}
      </div>

      {/* Upsell banner — shown when balance is low and user can still upgrade */}
      {!balanceLoading && !balanceError && balance !== null && balance < LOW_CREDIT_UI_THRESHOLD && effectiveTier !== 'max' && (
        <div className="usage-upsell-banner">
          <span className="usage-upsell-text">
            {effectiveTier === 'free'
              ? platform.isNative
                ? nativePrices['pro:monthly']
                  ? t('usage.upsell.lowBalanceFreeNative', {
                      credits: planTiersPro?.monthlyCredits?.toLocaleString(),
                      price: nativePrices['pro:monthly'],
                    })
                  : t('usage.upsell.lowBalanceFreeNative_noPrice', {
                      credits: planTiersPro?.monthlyCredits?.toLocaleString(),
                    })
                : t('usage.upsell.lowBalanceFree', {
                    credits: planTiersPro?.monthlyCredits?.toLocaleString(),
                    price: planTiersPro?.priceMonthly,
                  })
              : platform.isNative
                ? nativePrices['max:monthly']
                  ? t('usage.upsell.lowBalanceProNative', {
                      credits: planTiersMax?.monthlyCredits?.toLocaleString(),
                      price: nativePrices['max:monthly'],
                    })
                  : t('usage.upsell.lowBalanceProNative_noPrice', {
                      credits: planTiersMax?.monthlyCredits?.toLocaleString(),
                    })
                : t('usage.upsell.lowBalancePro', {
                    credits: planTiersMax?.monthlyCredits?.toLocaleString(),
                    price: planTiersMax?.priceMonthly,
                  })}
          </span>
          <button
            className="usage-upsell-btn"
            onClick={() => {
              onSwitchToPlan();
              trackEvent('upsell_banner_clicked', { from_tier: effectiveTier, balance });
            }}
          >
            {t('usage.upsell.viewPlans')}
          </button>
        </div>
      )}

      <div className="usage-topup-section">
        <p className="usage-topup-text">
          {t('usage.needMoreCredits')}
        </p>

        {/* Currency Selector - Card Style (web only, native uses store-local pricing) */}
        {!platform.isNative && (
          <div className="usage-currency-selector">
            {Object.entries(currencyConfig).map(([code, config]) => (
              <button
                key={code}
                className={`usage-currency-card ${selectedCurrency === code ? 'active' : ''}`}
                onClick={() => setSelectedCurrency(code)}
              >
                <div className="currency-symbol">{config.symbol} {config.label}</div>
                <div className="currency-methods">{t(`usage.paymentMethods.${config.paymentMethodKey}`)}</div>
              </button>
            ))}
          </div>
        )}

        <div className="usage-topup-buttons">
          <button
            className="usage-topup-btn"
            onClick={() => platform.isNative ? handleNativeTopUp(CREDIT_PACK_SMALL) : handleTopUp(CREDIT_PACK_SMALL)}
            disabled={topUpLoading !== null}
          >
            {topUpLoading === CREDIT_PACK_SMALL ? (
              <><Loader2 size={ICON_SIZE.sm} className="spin" /> {t('common.processing', { defaultValue: 'Processing...' })}</>
            ) : platform.isNative ? (
              t('usage.creditsPack', { count: CREDIT_PACK_SMALL.toLocaleString() })
            ) : (
              t('usage.creditsPrice', { count: CREDIT_PACK_SMALL.toLocaleString(), symbol: currencyInfo.symbol, price: currencyInfo.prices[CREDIT_PACK_SMALL] })
            )}
          </button>
          <button
            className="usage-topup-btn"
            onClick={() => platform.isNative ? handleNativeTopUp(CREDIT_PACK_LARGE) : handleTopUp(CREDIT_PACK_LARGE)}
            disabled={topUpLoading !== null}
          >
            {topUpLoading === CREDIT_PACK_LARGE ? (
              <><Loader2 size={ICON_SIZE.sm} className="spin" /> {t('common.processing', { defaultValue: 'Processing...' })}</>
            ) : platform.isNative ? (
              t('usage.creditsPack', { count: CREDIT_PACK_LARGE.toLocaleString() })
            ) : (
              t('usage.creditsPrice', { count: CREDIT_PACK_LARGE.toLocaleString(), symbol: currencyInfo.symbol, price: currencyInfo.prices[CREDIT_PACK_LARGE] })
            )}
          </button>
        </div>

        {/* Restore Purchases — required by App Store guidelines, native only */}
        {platform.isNative && (
          <button
            className="usage-restore-btn"
            onClick={handleRestorePurchases}
            disabled={restoreLoading || topUpLoading !== null}
          >
            {restoreLoading ? (
              <><Loader2 size={ICON_SIZE.xs} className="spin" /> {t('common.processing', { defaultValue: 'Processing...' })}</>
            ) : (
              <><RotateCcw size={ICON_SIZE.xs} /> {t('usage.restorePurchases')}</>
            )}
          </button>
        )}

        <div className="usage-contact-info">
          <span>{t('usage.contactQuestion')} </span>
          <a href="https://t.me/dimpurr" target="_blank" rel="noopener noreferrer">t.me/dimpurr</a>
        </div>
      </div>
    </>
  );
};

export default UsageBalanceTab;
